import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import Marquee from 'react-fast-marquee';
import { Trophy, Users, Calendar, ChevronRight, Zap, MapPin, Clock, Megaphone, Edit3, Circle, Trash2, PanelRightClose, PanelRightOpen } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE = process.env.REACT_APP_BACKEND_URL;

const defaultContent = {
  hero_title: 'A.C.C.T',
  hero_subtitle: 'Armor Critical Competitive Tournament',
  hero_tagline: 'Where champions are forged. Join the ultimate competitive gaming experience.',
  marquee_text: '',
  marquee_enabled: 'true',
  marquee_repeat: '3',
  features_title: 'Compete at the Highest Level',
  features_subtitle: 'Why Join A.C.C.T',
  feature_1_title: 'Competitive Play',
  feature_1_desc: 'Battle for glory in official tournaments',
  feature_2_title: 'Team Registration',
  feature_2_desc: 'Build your squad and compete',
  feature_3_title: 'Regular Events',
  feature_3_desc: 'Weekly matches and seasonal championships',
  teams_title: 'Competing Squads',
  teams_subtitle: 'Registered Teams',
  footer_copyright: '2026 A.C.C.T. All rights reserved.',
};

export default function HomePage() {
  const { isAuthenticated, isAdmin } = useAuth();
  const [teams, setTeams] = useState([]);
  const [tournament, setTournament] = useState(null);
  const [events, setEvents] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [content, setContent] = useState(defaultContent);
  const [editAnn, setEditAnn] = useState(null);
  const [annForm, setAnnForm] = useState({ title: '', content: '' });
  const [players, setPlayers] = useState([]);

  useEffect(() => {
    fetchTeams();
    fetchTournament();
    fetchEvents();
    fetchContent();
    fetchAnnouncements();
    fetchPlayers();
  }, []);

  const fetchTeams = async () => {
    try {
      const { data } = await axios.get(`${API}/teams`);
      setTeams(data);
    } catch (err) {
      console.error('Error fetching teams:', err);
    }
  };

  const fetchTournament = async () => {
    try {
      const { data } = await axios.get(`${API}/tournaments/active`);
      setTournament(data);
    } catch (err) {
      console.error('Error fetching tournament:', err);
    }
  };

  const fetchEvents = async () => {
    try {
      const { data } = await axios.get(`${API}/events`);
      setEvents(data);
    } catch (err) {
      console.error('Error fetching events:', err);
    }
  };

  const fetchContent = async () => {
    try {
      const { data } = await axios.get(`${API}/content`);
      setContent({ ...defaultContent, ...data });
    } catch (err) {
      console.error('Error fetching content:', err);
    }
  };

  const fetchAnnouncements = async () => {
    try {
      const { data } = await axios.get(`${API}/announcements`);
      setAnnouncements(data);
    } catch (err) {
      console.error('Error fetching announcements:', err);
    }
  };

  const fetchPlayers = async () => {
    try {
      const { data } = await axios.get(`${API}/players`);
      setPlayers(data);
    } catch (err) {
      console.error('Error fetching players:', err);
    }
  };

  const renderBadges = (badges) => {
    if (!badges || badges.length === 0) return null;
    return badges.map((b, idx) => (
      b.image_url ? (
        <img key={`${idx}-${b.design_id}`} src={`${BASE}${b.image_url}`} alt={b.name || b.tier} className="w-6 h-6 object-contain flex-shrink-0" title={b.name} />
      ) : null
    ));
  };

  const openEditAnn = (ann) => {
    setEditAnn(ann);
    setAnnForm({ title: ann.title, content: ann.content });
  };

  const handleAnnImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editAnn) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await axios.post(`${API}/admin/announcements/${editAnn.id}/image`, fd);
      setEditAnn({ ...editAnn, image_url: data.image_url });
      toast.success('Image uploaded');
      fetchAnnouncements();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    }
  };

  const handleAnnImageDelete = async () => {
    if (!editAnn) return;
    try {
      await axios.delete(`${API}/admin/announcements/${editAnn.id}/image`);
      setEditAnn({ ...editAnn, image_url: '' });
      toast.success('Image removed');
      fetchAnnouncements();
    } catch (err) {
      toast.error('Failed to remove image');
    }
  };

  const handleSaveAnn = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/announcements/${editAnn.id}`, annForm);
      toast.success('Announcement updated!');
      setEditAnn(null);
      fetchAnnouncements();
    } catch (err) {
      toast.error('Failed to update announcement');
    }
  };

  const handleDeleteAnn = async (annId, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this announcement?')) return;
    try {
      await axios.delete(`${API}/announcements/${annId}`);
      toast.success('Announcement deleted');
      fetchAnnouncements();
    } catch (err) {
      toast.error('Failed to delete announcement');
    }
  };

  const features = [
    { icon: Trophy, title: content.feature_1_title, desc: content.feature_1_desc },
    { icon: Users, title: content.feature_2_title, desc: content.feature_2_desc },
    { icon: Calendar, title: content.feature_3_title, desc: content.feature_3_desc },
  ];

  const onlinePlayers = players.filter(p => p.online);
  const offlinePlayers = players.filter(p => !p.online);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <main className="pt-20 relative">
      {/* Player Status Sidebar - only when signed in */}
      {isAuthenticated && (
      <>
        {/* Collapsed toggle tab */}
        {!sidebarOpen && (
          <button
            onClick={() => setSidebarOpen(true)}
            className="hidden lg:flex fixed right-0 top-24 z-40 items-center gap-1 bg-[#0a0a0a] border border-zinc-800 border-r-0 px-2 py-3 text-zinc-500 hover:text-cyan-400 transition-colors"
            data-testid="sidebar-open-btn"
          >
            <PanelRightOpen className="w-4 h-4" />
            <span className="text-[10px] uppercase tracking-wider font-bold writing-vertical" style={{writingMode: 'vertical-rl'}}>{onlinePlayers.length} Online</span>
          </button>
        )}
        {/* Sidebar panel */}
        <div className={`hidden lg:block fixed right-0 top-20 h-[calc(100vh-5rem)] bg-[#0a0a0a] border-l border-zinc-900 z-40 overflow-y-auto transition-all duration-300 ${sidebarOpen ? 'w-56' : 'w-0 overflow-hidden'}`} data-testid="player-sidebar">
        <div className="p-4 min-w-[14rem]">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-400">
              Players ({players.length})
            </h3>
            <button
              onClick={() => setSidebarOpen(false)}
              className="text-zinc-600 hover:text-cyan-400 transition-colors"
              data-testid="sidebar-close-btn"
            >
              <PanelRightClose className="w-4 h-4" />
            </button>
          </div>
          {onlinePlayers.length > 0 && (
            <div className="mb-4">
              <p className="text-xs text-green-500 uppercase tracking-wider mb-2 flex items-center gap-1">
                <Circle className="w-2 h-2 fill-green-500" />
                Online ({onlinePlayers.length})
              </p>
              <div className="space-y-1">
                {onlinePlayers.slice(0, 15).map((p) => (
                  <div key={p.id} className="flex items-center gap-2 py-1">
                    <Circle className="w-2 h-2 fill-green-500 text-green-500 flex-shrink-0" />
                    <span className="text-white text-xs truncate">{p.name}</span>
                    {p.team_tag && <span className="text-cyan-400 text-[10px] flex-shrink-0 font-mono">[{p.team_tag}]</span>}
                    {renderBadges(p.badges)}
                  </div>
                ))}
                {onlinePlayers.length > 15 && <p className="text-zinc-600 text-xs">+{onlinePlayers.length - 15} more</p>}
              </div>
            </div>
          )}
          {offlinePlayers.length > 0 && (
            <div>
              <p className="text-xs text-zinc-600 uppercase tracking-wider mb-2 flex items-center gap-1">
                <Circle className="w-2 h-2 text-zinc-700" />
                Offline ({offlinePlayers.length})
              </p>
              <div className="space-y-1">
                {offlinePlayers.slice(0, 15).map((p) => (
                  <div key={p.id} className="flex items-center gap-2 py-1">
                    <Circle className="w-2 h-2 text-zinc-700 flex-shrink-0" />
                    <span className="text-zinc-500 text-xs truncate">{p.name}</span>
                    {p.team_tag && <span className="text-cyan-400/50 text-[10px] flex-shrink-0 font-mono">[{p.team_tag}]</span>}
                    {renderBadges(p.badges)}
                  </div>
                ))}
                {offlinePlayers.length > 15 && <p className="text-zinc-700 text-xs">+{offlinePlayers.length - 15} more</p>}
              </div>
            </div>
          )}
        </div>
      </div>
      </>
      )}

      {/* Main content with right margin for sidebar */}
      <div className={isAuthenticated && sidebarOpen ? "lg:pr-56" : ""}>
      {/* Hero Section */}
      <section 
        className="relative min-h-[90vh] flex items-center justify-center overflow-hidden"
        data-testid="hero-section"
        style={{
          backgroundImage: `url(https://customer-assets.emergentagent.com/job_accl-league/artifacts/wwsftz6h_Screenshot%202026-02-06%20000916.png)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 hero-overlay" />
        
        <div className="relative z-10 text-center px-6 w-full max-w-4xl mx-auto">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-cyan-400 mb-6 animate-fade-in-up">
            {content.hero_subtitle}
          </p>
          
          <h1 className="font-heading text-5xl sm:text-6xl lg:text-8xl font-black tracking-tighter uppercase text-white mb-6 animate-fade-in-up animate-delay-100 text-glow-cyan">
            {content.hero_title}
          </h1>
          
          <p className="text-lg sm:text-xl text-zinc-400 max-w-xl mx-auto mb-10 animate-fade-in-up animate-delay-200">
            {content.hero_tagline}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center flex-wrap animate-fade-in-up animate-delay-300">
            <a href="#events-section">
              <Button 
                variant="outline"
                className="border-yellow-500/50 text-yellow-500 hover:bg-yellow-500/10 hover:border-yellow-500 rounded-none px-8 py-6 text-lg"
                data-testid="upcoming-events-hero-btn"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Upcoming Events
              </Button>
            </a>
            {isAuthenticated ? (
              <Link to="/teams">
                <Button 
                  variant="outline"
                  className="border-cyan-400/50 text-cyan-400 hover:bg-cyan-400/10 hover:border-cyan-400 rounded-none px-8 py-6 text-lg"
                  data-testid="view-teams-hero-btn"
                >
                  <Zap className="w-5 h-5 mr-2" />
                  View Teams
                </Button>
              </Link>
            ) : (
              <Link to="/register">
                <Button 
                  variant="outline"
                  className="border-cyan-400/50 text-cyan-400 hover:bg-cyan-400/10 hover:border-cyan-400 rounded-none px-8 py-6 text-lg"
                  data-testid="join-now-hero-btn"
                >
                  <Zap className="w-5 h-5 mr-2" />
                  Join Now
                </Button>
              </Link>
            )}
            <Link to="/bracket">
              <Button 
                variant="outline"
                className="border-zinc-500/50 text-zinc-300 hover:bg-zinc-500/10 hover:border-zinc-300 rounded-none px-8 py-6 text-lg"
                data-testid="view-bracket-hero-btn"
              >
                View Bracket
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>

          {/* Announcements inside hero */}
          {announcements.length > 0 && (
            <div className="mt-12 max-w-2xl mx-auto animate-fade-in-up animate-delay-400">
              <div className="space-y-3">
                {announcements.map((ann) => (
                  <div
                    key={ann.id}
                    className={`bg-black/50 backdrop-blur-sm border-l-4 border-l-yellow-500 border-y border-r border-y-white/10 border-r-white/10 p-4 text-left ${isAdmin ? 'cursor-pointer hover:border-yellow-500/50 transition-colors' : ''}`}
                    data-testid={`announcement-${ann.id}`}
                    onClick={() => isAdmin && openEditAnn(ann)}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Megaphone className="w-4 h-4 text-yellow-500 flex-shrink-0" />
                      <h3 className="font-bold text-white text-sm flex-1">{ann.title}</h3>
                      {isAdmin && (
                        <button
                          onClick={(e) => handleDeleteAnn(ann.id, e)}
                          className="text-red-500/50 hover:text-red-400 transition-colors"
                          title="Delete announcement"
                          data-testid={`delete-ann-${ann.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {isAdmin && <Edit3 className="w-3.5 h-3.5 text-yellow-500/50" />}
                    </div>
                    <p className="text-zinc-400 text-sm whitespace-pre-wrap pl-6">{ann.content}</p>
                    {ann.image_url && (
                      <img
                        src={`${BASE}${ann.image_url}`}
                        alt={ann.title}
                        className="mt-3 max-h-64 w-auto object-contain border border-white/10 ml-6"
                        data-testid={`announcement-image-${ann.id}`}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-zinc-600 rounded-full flex justify-center pt-2">
            <div className="w-1.5 h-3 bg-cyan-400 rounded-full" />
          </div>
        </div>
      </section>

      {/* Teams Marquee */}
      {content.marquee_enabled !== 'false' && (teams.length > 0 || content.marquee_text) && (
        <section className="py-6 bg-[#0a0a0a] border-y border-zinc-900" data-testid="teams-marquee">
          <Marquee gradient={false} speed={40}>
            {content.marquee_text ? (
              Array.from({ length: Math.max(1, Math.min(20, parseInt(content.marquee_repeat) || 3)) }).map((_, i) => (
                <span key={i} className="font-mono text-sm uppercase tracking-wider text-cyan-400 mx-8">
                  {content.marquee_text} &nbsp;&nbsp;///&nbsp;&nbsp;
                </span>
              ))
            ) : (
              Array.from({ length: Math.max(1, Math.min(20, parseInt(content.marquee_repeat) || 3)) }).flatMap((_, ri) =>
                teams.map((team, idx) => (
                  <span 
                    key={`${ri}-${team.id}-${idx}`}
                    className="font-mono text-sm uppercase tracking-wider text-cyan-400 mx-8"
                  >
                    [{team.tag}] {team.name} ///
                  </span>
                ))
              )
            )}
          </Marquee>
        </section>
      )}

      {/* Features Section */}
      <section className="py-24 px-6 bg-[#050505]" data-testid="features-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-400 mb-4">
              {content.features_subtitle}
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold uppercase text-white">
              {content.features_title}
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, idx) => (
              <div 
                key={idx}
                className="bg-[#121212] border border-white/5 p-8 card-hover"
                style={{ animationDelay: `${idx * 0.1}s` }}
                data-testid={`feature-card-${idx}`}
              >
                <feature.icon className="w-10 h-10 text-cyan-400 mb-6" />
                <h3 className="font-heading text-xl font-bold text-white mb-3">
                  {feature.title}
                </h3>
                <p className="text-zinc-400">
                  {feature.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tournament Status */}
      {tournament && (
        <section className="py-24 px-6 bg-[#0a0a0a]" data-testid="tournament-section">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-500/10 border border-green-500/30 mb-6">
              <div className="w-2 h-2 rounded-full bg-green-500 pulse-live" />
              <span className="text-xs font-bold uppercase tracking-wider text-green-500">
                {tournament.status === 'active' ? 'Tournament Active' : 'Registration Open'}
              </span>
            </div>
            
            <h2 className="font-heading text-3xl sm:text-4xl font-bold uppercase text-white mb-4">
              {tournament.name}
            </h2>
            <p className="text-zinc-400 mb-8">
              {tournament.team_count} Teams {tournament.matches?.length ? `\u2022 ${tournament.matches.length} Matches` : ''}
            </p>
            
            <Link to="/bracket">
              <Button 
                className="bg-cyan-400 text-black font-bold uppercase tracking-wide hover:bg-cyan-300 rounded-none px-8 py-4"
                data-testid="view-tournament-bracket-btn"
              >
                View Tournament Bracket
              </Button>
            </Link>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="py-12 px-6 bg-[#050505] border-t border-zinc-900" data-testid="footer">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <span className="font-heading text-lg font-bold text-cyan-400">A.C.C.T</span>
              <span className="text-zinc-600">|</span>
              <span className="text-zinc-500 text-sm">Armor Critical Competitive Tournament</span>
            </div>
            <div className="flex items-center gap-6">
              <Link to="/rules" className="text-sm text-zinc-500 hover:text-cyan-400 transition-colors">
                Rules
              </Link>
              <Link to="/teams" className="text-sm text-zinc-500 hover:text-cyan-400 transition-colors">
                Teams
              </Link>
              <Link to="/bracket" className="text-sm text-zinc-500 hover:text-cyan-400 transition-colors">
                Bracket
              </Link>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-zinc-900 text-center">
            <p className="text-xs text-zinc-600">
              &copy; {content.footer_copyright}
            </p>
          </div>
        </div>
      </footer>
      </div>{/* Close main content wrapper */}

      {/* Edit Announcement Dialog */}
      <Dialog open={!!editAnn} onOpenChange={() => setEditAnn(null)}>
        <DialogContent className="bg-[#121212] border-zinc-800 rounded-none max-w-md" data-testid="edit-announcement-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-white uppercase">
              Edit Announcement
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveAnn} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm uppercase tracking-wide">Title</Label>
              <Input
                value={annForm.title}
                onChange={(e) => setAnnForm({ ...annForm, title: e.target.value })}
                required
                className="bg-black border-zinc-800 text-white placeholder:text-zinc-600 rounded-none h-12 focus:border-cyan-400"
                data-testid="edit-ann-title"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm uppercase tracking-wide">Content</Label>
              <Textarea
                value={annForm.content}
                onChange={(e) => setAnnForm({ ...annForm, content: e.target.value })}
                required
                className="bg-black border-zinc-800 text-white placeholder:text-zinc-600 rounded-none min-h-[100px] focus:border-cyan-400 resize-none"
                data-testid="edit-ann-content"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm uppercase tracking-wide">Image (Optional)</Label>
              {editAnn?.image_url ? (
                <div className="flex items-start gap-3">
                  <img src={`${BASE}${editAnn.image_url}`} alt="" className="max-h-32 w-auto object-contain border border-zinc-800" />
                  <Button type="button" onClick={handleAnnImageDelete} variant="outline" size="sm" className="border-red-500/30 text-red-400 hover:bg-red-500/10 rounded-none h-9" data-testid="remove-ann-image-btn">
                    Remove
                  </Button>
                </div>
              ) : null}
              <input
                type="file"
                accept="image/*"
                onChange={handleAnnImageUpload}
                className="block w-full text-zinc-400 text-sm file:mr-3 file:py-2 file:px-4 file:rounded-none file:border-0 file:bg-yellow-500 file:text-black file:font-bold file:cursor-pointer hover:file:bg-yellow-400"
                data-testid="ann-image-upload-input"
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-yellow-500 text-black font-bold uppercase tracking-wide hover:bg-yellow-400 rounded-none h-12"
              data-testid="save-ann-button"
            >
              Update Announcement
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </main>
  );
}
